import { NOTIFICATION_TYPE } from '@vedmarket/ui-kit'
import {
  APPLICATION_CHECK_STATUS,
  APPLICATION_CLARIFICATION_STATUS,
  APPLICATION_OUTCOME,
  APPLICATION_PROPOSAL_STATUS,
  APPLICATION_STATUS,
} from '../../../../config/constants'

import { clientRefused } from './manager-workflow'

const PREPARATION_STATUSES = new Set<string>([ APPLICATION_STATUS.PREPARING, APPLICATION_STATUS.AWAITING_CLIENT ])

/** После завершения подготовки: пакет и предложение остаются в ленте менеджера до закрытия. */
const MANAGER_PIPELINE_STATUSES = new Set<string>([
  APPLICATION_STATUS.PREPARATION_COMPLETE,
  APPLICATION_STATUS.AWAITING_EXECUTOR_PROPOSAL,
  APPLICATION_STATUS.PROPOSAL_READY,
  APPLICATION_STATUS.AWAITING_EXECUTOR_CONTACT,
  APPLICATION_STATUS.AT_EXECUTOR,
  APPLICATION_STATUS.CLOSED,
])

const EXECUTOR_WORK_STATUSES = new Set<string>([
  APPLICATION_STATUS.PREPARING,
  APPLICATION_STATUS.AWAITING_CLIENT,
  APPLICATION_STATUS.PREPARATION_COMPLETE,
  APPLICATION_STATUS.AWAITING_EXECUTOR_PROPOSAL,
])

export function isProposalPublished(app: ApplicationDetail): boolean {
  return app.proposal?.status === APPLICATION_PROPOSAL_STATUS.PUBLISHED
}

export function canClientActOnProposal(app: ApplicationDetail): boolean {
  return (
    isProposalPublished(app)
    && !app.proposal?.declined_at
    && !app.client_proposal_accepted_at
  )
}

export function showClientStatusHint(managerView: boolean): boolean {
  return !managerView
}

export function showClientCheckItems({ app, managerView }: ApplicationShowClientCheckItemsArgs): boolean {
  if (managerView) return false
  if (!PREPARATION_STATUSES.has(app.status)) return false
  return app.check_items.some((item) => item.status !== APPLICATION_CHECK_STATUS.COMPLETED)
}

export function clientCheckItemsToShow(app: ApplicationDetail): ApplicationCheckItem[] {
  if (app.status === APPLICATION_STATUS.AWAITING_CLIENT) {
    const kindsWithOpenClarification = new Set(
      app.clarifications
        .filter((c) => c.status === APPLICATION_CLARIFICATION_STATUS.OPEN && c.field_hint)
        .map((c) => c.field_hint),
    )
    if (kindsWithOpenClarification.size > 0) {
      return app.check_items.filter((item) => kindsWithOpenClarification.has(item.kind))
    }
  }
  return app.check_items.filter((item) => item.status !== APPLICATION_CHECK_STATUS.COMPLETED)
}

export function showClientClarifications({ app, managerView }: ApplicationShowClientClarificationsArgs): boolean {
  if (managerView) return false
  if (app.status === APPLICATION_STATUS.AWAITING_CLIENT) {
    return app.clarifications.length > 0
  }
  if (PREPARATION_STATUSES.has(app.status)) {
    return app.clarifications.some((c) => c.status === APPLICATION_CLARIFICATION_STATUS.OPEN)
  }
  return false
}

/** Клиент на шаге «ознакомиться с пакетом / запросить предложение» (README §5–6). */
export function showClientPackageTransferActions({ app, managerView }: ApplicationShowClientPackageTransferActionsArgs): boolean {
  return !managerView && app.status === APPLICATION_STATUS.PREPARATION_COMPLETE
}

export function showClientProposal({ app, managerView }: ApplicationShowClientProposalArgs): boolean {
  if (managerView) return false
  if (!app.proposal) return false
  if (isProposalPublished(app)) return true
  if (app.proposal.declined_at) return true
  return false
}

export function showClientActions({ app, managerView }: ApplicationShowClientActionsArgs): boolean {
  const isClientProposalReady = !managerView && app.status === APPLICATION_STATUS.PROPOSAL_READY
  return isClientProposalReady && canClientActOnProposal(app)
}

export function showClientContacts({ app, managerView }: ApplicationShowClientContactsArgs): boolean {
  return !managerView && Boolean(app.contacts_revealed_at)
}

/** Итог закрытой заявки — компактный баннер в шапке (клиент и менеджер). */
export function showClosedOutcomeHeaderBanner(app: ApplicationDetail): boolean {
  return app.status === APPLICATION_STATUS.CLOSED && app.outcome !== APPLICATION_OUTCOME.UNKNOWN
}

/** Блок отказа в шапке (менеджер); у клиента отказ показываем на карточке предложения. */
export function showProposalDeclinedNoticeAtTop(
  app: ApplicationDetail,
  managerView = false,
): boolean {
  if (!showClosedOutcomeHeaderBanner(app) || !app.proposal?.declined_at) {
    return false
  }
  return managerView
}

export function showClientRequestStatus({ app, managerView }: ApplicationShowClientRequestStatusArgs): boolean {
  if (managerView) return false
  if (app.status === APPLICATION_STATUS.PROPOSAL_READY) {
    return Boolean(app.contact_exchange_requested_at)
  }
  return false
}

export function showManagerTakeWork({ app, managerView }: ApplicationShowManagerTakeWorkArgs): boolean {
  return managerView && !app.manager_id
}

/** Этап «Исполнитель» в ленте менеджера. */
export function showManagerExecutorWorkflowStep({ app, managerView }: ApplicationShowManagerExecutorArgs): boolean {
  if (!managerView || !app.manager_id) {
    return false
  }
  return PREPARATION_STATUSES.has(app.status) || EXECUTOR_WORK_STATUSES.has(app.status)
}

function showManagerExecutorEditorBase({ app, managerView }: ApplicationShowManagerExecutorArgs): boolean {
  if (!managerView || !app.manager_id || !app.preparation_completed_at) {
    return false
  }
  return EXECUTOR_WORK_STATUSES.has(app.status)
}

/** Исполнитель не был назначен при завершении подготовки — назначить вручную. */
export function showManagerExecutorAssignForm({ app, managerView }: ApplicationShowManagerExecutorArgs): boolean {
  if (!showManagerExecutorEditorBase({ app, managerView })) {
    return false
  }
  return !app.executor_company_id
}

/** Смена уже назначенного исполнителя. */
export function showManagerExecutorChangeForm({ app, managerView }: ApplicationShowManagerExecutorArgs): boolean {
  if (!showManagerExecutorEditorBase({ app, managerView })) {
    return false
  }
  return Boolean(app.executor_company_id)
}

export function showManagerPreparationTools({ app, managerView }: ApplicationShowManagerPreparationToolsArgs): boolean {
  return managerView && Boolean(app.manager_id) && PREPARATION_STATUSES.has(app.status)
}

export function showManagerProposalTools({ app, managerView }: ApplicationShowManagerProposalToolsArgs): boolean {
  if (!managerView || !app.manager_id) return false
  if (app.proposal?.declined_at) return true
  if (!MANAGER_PIPELINE_STATUSES.has(app.status)) return false
  if (app.status === APPLICATION_STATUS.CLOSED && !app.proposal) return false
  return true
}

/** Пакет проверки для менеджера после завершения подготовки (только просмотр). */
export function showManagerPackageReview({ app, managerView }: ApplicationShowManagerPackageReviewArgs): boolean {
  if (!managerView || !app.manager_id) return false
  return MANAGER_PIPELINE_STATUSES.has(app.status)
}

/** Исполнитель уже назначен — показываем в ленте после этапа выбора. */
export function showManagerAssignedExecutor({ app, managerView }: ApplicationShowManagerAssignedExecutorArgs): boolean {
  if (!managerView || !app.manager_id) return false
  if (EXECUTOR_WORK_STATUSES.has(app.status)) return false
  return Boolean(app.executor_company?.name?.trim()) || Boolean(app.executor_company_id)
}

/** Контакты уже переданы клиенту. */
export function showManagerContactsSummary({ app, managerView }: ApplicationShowManagerContactsSummaryArgs): boolean {
  return Boolean(managerView && app.manager_id && app.contacts_revealed_at)
}

/** Контакты, которые клиент передал при принятии предложения. */
export function showManagerClientContacts({ app, managerView }: ApplicationShowManagerContactsSummaryArgs): boolean {
  if (!managerView || !app.manager_id) {
    return false
  }
  return Boolean(app.client_proposal_accepted_at || app.contact_exchange_requested_at)
}

/** Публикация клиенту — только после передачи пакета исполнителю (домен). */
export function canManagerPublishProposal(app: ApplicationDetail): boolean {
  return app.status !== APPLICATION_STATUS.PREPARATION_COMPLETE
}

/** Черновик нельзя править после согласия клиента на обмен контактами и далее по цепочке. */
export function canManagerEditProposalDraft(app: ApplicationDetail): boolean {
  if (app.proposal?.declined_at) {
    return false
  }
  if (app.contact_exchange_requested_at) {
    return false
  }
  if (app.contacts_revealed_at) {
    return false
  }
  const lockedStatuses = new Set<string>([
    APPLICATION_STATUS.AWAITING_EXECUTOR_CONTACT,
    APPLICATION_STATUS.AT_EXECUTOR,
    APPLICATION_STATUS.CLOSED,
  ])
  if (lockedStatuses.has(app.status) && isProposalPublished(app)) {
    return false
  }
  return true
}

export function showManagerRevealContacts({ app, managerView }: ApplicationShowManagerRevealContactsArgs): boolean {
  if (!managerView || !app.manager_id) return false
  if (app.contacts_revealed_at) return false
  return Boolean(app.contact_exchange_requested_at)
    || app.status === APPLICATION_STATUS.AWAITING_EXECUTOR_CONTACT
    || app.status === APPLICATION_STATUS.AT_EXECUTOR
}

export function showManagerOutcome({ app, managerView }: ApplicationShowManagerOutcomeArgs): boolean {
  if (!managerView || !app.manager_id) return false
  return app.status === APPLICATION_STATUS.AT_EXECUTOR || app.status === APPLICATION_STATUS.CLOSED
}

export function showManagerClarificationsSummary({ app, managerView }: ApplicationShowManagerClarificationsSummaryArgs): boolean {
  if (!managerView) return false
  return app.clarifications.length > 0 && PREPARATION_STATUSES.has(app.status)
}

export function statusHintKey({ status, managerView }: ApplicationStatusHintKeyArgs): string {
  const prefix = managerView ? 'applications__text__hintMgr_' : 'applications__text__hint_'
  return `${prefix}${status}`
}

export function applicationNotificationType(app: ApplicationDetail): NOTIFICATION_TYPE {
  switch (app.status) {
    case APPLICATION_STATUS.AWAITING_CLIENT:
    case APPLICATION_STATUS.PROPOSAL_READY:
      return NOTIFICATION_TYPE.WARNING
    case APPLICATION_STATUS.PREPARATION_COMPLETE:
      return NOTIFICATION_TYPE.SUCCESS
    case APPLICATION_STATUS.CLOSED:
      return clientRefused(app) ? NOTIFICATION_TYPE.ERROR : NOTIFICATION_TYPE.SUCCESS
    default:
      return NOTIFICATION_TYPE.INFO
  }
}
